import { Component } from '@theme/component';
import { cycleFocus } from '@theme/focus';

/**
 * @typedef {Object} FeaturedCollectionTabsRefs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLAnchorElement} [viewAllLink]
 */

/** @extends {Component<FeaturedCollectionTabsRefs>} */
class FeaturedCollectionTabs extends Component {
  /** Panels always render with blocks; tab buttons are optional (avoid MissingRefError if counts diverge). */
  requiredRefs = ['panels'];

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();

    const { tabs, panels } = this.refs;
    if (!panels?.length) return;

    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    if (tabs?.length) {
      for (const [i, tab] of tabs.entries()) {
        tab.addEventListener('click', () => this.#activate(i), opts);
      }
      this.addEventListener('keydown', this.#onKeydown, opts);
      this.#syncTabsFromPanels();
      const initial = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      this.#activate(initial >= 0 ? initial : 0, false);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    this.#syncTabsFromPanels();
    const initial = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.#activate(initial >= 0 ? initial : 0, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /** Copy labels and URLs from tabpanels (source of truth after theme editor section updates). */
  #syncTabsFromPanels() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    for (const tab of tabs) {
      const panelId = tab.getAttribute('aria-controls');
      if (!panelId) continue;

      const panel = document.getElementById(panelId);
      if (!(panel instanceof HTMLElement)) continue;

      const label = panel.dataset.tabLabel;
      const url = panel.dataset.collectionUrl;
      if (label) tab.textContent = label;
      if (url) tab.dataset.collectionUrl = url;
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [moveFocusToTab]
   */
  #activate(index, moveFocusToTab = true) {
    this.#syncTabsFromPanels();

    const { tabs, panels, viewAllLink } = this.refs;
    if (!panels?.length) return;
    if (!tabs?.length) return;

    const i = Math.max(0, Math.min(index, tabs.length - 1, panels.length - 1));

    for (const [j, tab] of tabs.entries()) {
      const selected = j === i;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    }

    for (const [j, panel] of panels.entries()) {
      const show = j === i;

      if (show) {
        panel.hidden = false;
        panel.removeAttribute('inert');
      } else {
        panel.hidden = true;
        panel.setAttribute('inert', '');
      }
    }

    if (viewAllLink instanceof HTMLAnchorElement) {
      const url = tabs[i]?.dataset.collectionUrl;
      if (url) {
        viewAllLink.href = url;
      }
    }

    if (moveFocusToTab) {
      tabs[i]?.focus();
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const i = tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (i < 0) return;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        event.preventDefault();
        cycleFocus(tabs, 1);
        const next = tabs.findIndex((t) => t === document.activeElement);
        if (next >= 0) this.#activate(next, false);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        event.preventDefault();
        cycleFocus(tabs, -1);
        const prev = tabs.findIndex((t) => t === document.activeElement);
        if (prev >= 0) this.#activate(prev, false);
        break;
      }
      case 'Home': {
        event.preventDefault();
        this.#activate(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        this.#activate(tabs.length - 1);
        break;
      }
    }
  };
}

if (!customElements.get('featured-collection-tabs-component')) {
  customElements.define('featured-collection-tabs-component', FeaturedCollectionTabs);
}
